// src/App.js
import React, { useEffect, useState, createContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import LandingPage from "./components/LandingPage";
import ClientBooking from "./components/ClientBooking";
import BookingsPage from "./components/BookingsPage";
import Login from "./components/Login";
import AdminDashboard from "./components/AdminDashboard";
import RoomsPage from "./components/RoomsPage";
import TenantsPage from "./components/TenantsPage";
import PaymentsPage from "./components/PaymentsPage";
import TenantProfilePage from "./components/TenantProfilePage";
import UsersPage from "./components/UsersPage";

import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";

import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

// ⭐ NEW — Create context so all pages can access user + role
export const AppUserContext = createContext(null);

function App() {
  const [appUser, setAppUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // ⭐ NEW — Load Firebase user + Firestore role
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (fbUser) => {
      if (!fbUser) {
        setAppUser(null);
        setLoadingUser(false);
        return;
      }

      // Load Firestore user document
      const snap = await getDoc(doc(db, "users", fbUser.uid));

      if (snap.exists()) {
        setAppUser({
          uid: fbUser.uid,
          email: fbUser.email,
          role: snap.data().role, // SUPERADMIN, ADMIN, STAFF
        });
      } else {
        setAppUser({
          uid: fbUser.uid,
          email: fbUser.email,
          role: "STAFF", // fallback
        });
      }

      setLoadingUser(false);
    });

    return () => unsubscribe();
  }, []);

  // ⭐ NEW — Debug role in console
  useEffect(() => {
    console.log("🔍 Current appUser:", appUser);
  }, [appUser]);

  if (loadingUser) {
    return <div className="p-6">Loading user…</div>;
  }

  return (
    <AppUserContext.Provider value={appUser}>
      <Router>
        <Routes>
          {/* PUBLIC ROUTES */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/booking" element={<ClientBooking />} />
          <Route path="/login" element={<Login />} />

          {/* ADMIN DASHBOARD */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* ROOMS */}
          <Route
            path="/dashboard/rooms"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <RoomsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* TENANTS LIST */}
          <Route
            path="/dashboard/tenants"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <TenantsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* TENANT PROFILE */}
          <Route
            path="/dashboard/tenants/:id"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <TenantProfilePage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* PAYMENTS */}
          <Route
            path="/dashboard/payments"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <PaymentsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* USERS (superadmin only) */}
          <Route
            path="/dashboard/users"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <UsersPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/bookings"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <BookingsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
              
          {/* CATCH ALL */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AppUserContext.Provider>
  );
}

export default App;
